import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  styled, Avatar,
} from "@mui/material";
import { VolumeUp, Timer } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { SectionType } from "@app/constants";

const StyledAppBar = styled(AppBar)({
  backgroundColor: "#fff",
  color: "#000",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
});

const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
  position: "relative",
});

const LeftSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "24px",
});

const TimerSection = styled(Box)({
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const RightSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const VolumeInput = styled("input")(({ theme }) => ({
  width: "120px",
  [theme.breakpoints.down("sm")]: {
    width: "100px",
  },
}));

interface ExamHeaderProps {
  duration?: string; // "40 phút" format
  sectionType: SectionType;
  onSubmit: () => void;
}

function ExamHeader({ duration = "40 phút", sectionType, onSubmit }: ExamHeaderProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [volume, setVolume] = useState<number>(100);

  useEffect(() => {
    const minutes = parseInt(duration.split(" ")[0]);
    const totalSeconds = minutes * 60;

    const endTime = Date.now() + totalSeconds * 1000;

    setTimeLeft(totalSeconds);

    const timer = setInterval(() => {
      const secondsLeft = Math.max(0, Math.floor((endTime - Date.now()) / 1000));

      setTimeLeft(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(timer);
        onSubmit();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [sectionType]);

  useEffect(() => {
    const audioElements = document.getElementsByTagName("audio");

    Array.from(audioElements).forEach(audio => {
      audio.volume = volume / 100;
    });
  }, [volume]);

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(event.target.value);

    setVolume(newVolume);
  };

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <StyledAppBar position="fixed">
      <StyledToolbar>
        <LeftSection>
          <Avatar src="/logo.jpg" variant="square">
          </Avatar>
        </LeftSection>

        <TimerSection>
          <Timer sx={{ color: timeLeft <= 180 ? "#ff1744" : "inherit" }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: timeLeft <= 180 ? "#ff1744" : "inherit",
            }}
          >
            {formatTime(timeLeft)}
          </Typography>
        </TimerSection>

        <RightSection>
          {sectionType === SectionType.LISTENING && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VolumeUp />
              <VolumeInput 
                type="range" 
                min="0" 
                max="100" 
                value={volume}
                onChange={handleVolumeChange}
              />
            </Box>
          )}
        </RightSection>
      </StyledToolbar>
    </StyledAppBar>
  );
}

export default ExamHeader;
